import { Navigate, useLocation } from "react-router-dom";
import { Flex } from "antd";
import { Spinner } from "@libertify/ui";
import { VideoSummaryStats } from "types/";
import { FeatureFlags } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeature";
import { useAnalyticsPage } from "./hooks/useAnalyticsPage";
import { useAnalyticsBackState } from "./hooks/useAnalyticsBack";
import { AnalyticsPageLayout } from "../components/analytics/AnalyticsPageLayout";
import { AnalyticsSubPageLayout } from "../components/analytics/AnalyticsSubPageLayout";
import { VideoPanel } from "../components/analytics/addons/VideoPanel";
import { VIDEO_PILL } from "../components/analytics/addons/AddonPills";
import { useProjectAnalyticsTabs } from "../components/analytics/addons/hooks/useAddonsAnalytics";

const T_PREFIX = "media.projects.analytics.videoExplainer";

/** The Video tab, shown when the project has no other add-on. */
export function VideoExplainerAnalyticsPage() {
  const page = useAnalyticsPage<VideoSummaryStats>({
    analyticsType: "video",
    versioned: true,
    summaryFollowsVersion: true,
  });
  const { search } = useLocation();
  const backState = useAnalyticsBackState();
  const isAddonsEnabled = useFeatureFlag(FeatureFlags.AddonsAnalytics);
  const tabs = useProjectAnalyticsTabs(page.projectId, isAddonsEnabled);

  // Wait for the add-ons check, so a direct link doesn't flash this page
  // before redirecting. The page shell stays, with its back button.
  if (isAddonsEnabled && page.projectId && tabs === undefined) {
    return (
      <AnalyticsPageLayout
        authKey="videoExplainerAnalytics"
        projectId={page.projectId}
        projectTitle={page.project?.title}
        onBack={page.handleBack}
        dateRange={page.dateRange}
        version={page.version?.requested}
      >
        <Flex justify="center" style={{ padding: 48 }}>
          <Spinner size="lg" />
        </Flex>
      </AnalyticsPageLayout>
    );
  }

  // With add-ons, the video lives under the Add-ons tab (e.g. the Overview
  // video card or an old link): open it there on the Video pill.
  if (isAddonsEnabled && tabs?.hasAddons) {
    return (
      <Navigate
        to={{ pathname: "../addons", search }}
        relative="path"
        replace
        state={{ ...backState, addonPill: VIDEO_PILL }}
      />
    );
  }

  // No video, no tab: back to the overview.
  if (isAddonsEnabled && tabs && !tabs.hasVideo) {
    return (
      <Navigate
        to={{ pathname: "..", search }}
        relative="path"
        replace
        state={backState}
      />
    );
  }

  return (
    <AnalyticsSubPageLayout
      authKey="videoExplainerAnalytics"
      translationPrefix={T_PREFIX}
      page={page}
    >
      <VideoPanel
        projectId={page.projectId}
        project={page.project}
        dateRange={page.dateRange}
        setDateRange={page.setDateRange}
        version={page.version}
        showDatePicker={false}
      />
    </AnalyticsSubPageLayout>
  );
}
